import BasePlayer from './BasePlayer';
import { getNextPlayRequestId } from '../common/channelPlayerState';
import { createHttpStreamCompletedHandler, createHttpStreamErrorHandler, markChannelStreamReady } from '../common/channelStreamUrl';
import {
  applyAvplayDisplayMode,
  getVideoDimensionsFromAvplay,
  parseTizenVideoResolution,
} from '../common/channelVideoAspect';
import { applyAvplayBuffering } from '../common/avplayBuffering';

/**
 * Samsung Tizen player implementation
 * Uses window.webapis.epavplay API with complex state management
 */
class TizenPlayer extends BasePlayer {
  constructor() {
    super();
    this.maxRetries = 3;
    this._lastPreviewCoords = null;
    this._lastVideoDimensions = null;
    this._currentPlayUrl = null;
    this._initializePlayerObject();
  }

  /**
   * Initialize Tizen player object - moved from BaseChannelTemplate
   */
  _initializePlayerObject() {
    const playerState = this.getPlayerState();

    // Only initialize if not already done
    if (!playerState.gAVplayObj) {
      const gAVplayObj = window.webapis.epavplay;
      this.updatePlayerState({ gAVplayObj });
    }
  }

  supportsFullscreenToggle() {
    return true;
  }

  /**
   * Calculate preview coordinates based on av-player element's actual position
   * Falls back to default coordinates if element not found or positioned
   */
  _calculatePreviewCoords() {
    // Prefer the UI's preview area anchor when present (Warwick layout).
    // This keeps native buffering/loader centered within the preview panel.
    const player =
      document.querySelector('[data-tizen-video-area]') || document.getElementById('av-player');
    
    if (player) {
      // Force layout recalculation before getting rect
      // eslint-disable-next-line no-unused-expressions
      player.offsetHeight; // Force reflow
      
      const rect = player.getBoundingClientRect();
      
      console.log('Tizen: Raw getBoundingClientRect:', {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        bottom: rect.bottom,
        right: rect.right
      });
      
      // If element has valid dimensions, use its position
      if (rect.width > 0 && rect.height > 0) {
        const coords = {
          x: Math.round(rect.left),
          y: Math.round(rect.top),
          width: Math.round(rect.width),
          height: Math.round(rect.height)
        };
        
        console.log('Tizen: Using calculated preview coords:', coords);
        return coords;
      }
    }
    
    // Fallback to default coordinates (old channelsList.js style)
    console.log('Tizen: Using fallback preview coords');
    return { x: 500, y: 300, width: 854, height: 480 };
  }

  _isPlausiblePreviewCoords(coords) {
    return (
      coords &&
      coords.x >= 300 &&
      coords.width >= 400 &&
      coords.width <= 1500 &&
      coords.height >= 200
    );
  }

  async _waitForStablePreviewCoords() {
    var prev = null;
    for (let i = 0; i < 24; i++) {
      const coords = this._calculatePreviewCoords();
      if (this._isPlausiblePreviewCoords(coords)) {
        if (
          prev &&
          prev.x === coords.x &&
          prev.y === coords.y &&
          prev.width === coords.width &&
          prev.height === coords.height
        ) {
          return coords;
        }
        prev = coords;
      } else {
        prev = null;
      }
      await this.delay(50);
    }
    return this._calculatePreviewCoords();
  }

  _applyPreviewRect(coords, isFullScreen) {
    const playerState = this.getPlayerState();
    if (!playerState.gAVplayObj || !coords) {
      return;
    }
    try {
      playerState.gAVplayObj.setDisplayRect(coords.x, coords.y, coords.width, coords.height);
      this._lastPreviewCoords = coords;
      this._applyHttpAspectMode(playerState.playRequestId);
    } catch (e) {
      console.warn('Tizen: setDisplayRect failed:', e.message || e);
    }
  }

  _applyHttpAspectMode(myRequestId) {
    if (myRequestId !== this.getPlayerState().playRequestId) {
      return;
    }

    const playerState = this.getPlayerState();
    const avplay = playerState.gAVplayObj;
    if (!avplay) {
      return;
    }

    var dimensions = this._lastVideoDimensions;
    if (!dimensions) {
      dimensions = getVideoDimensionsFromAvplay(avplay);
      if (dimensions) {
        this._lastVideoDimensions = dimensions;
      }
    }

    applyAvplayDisplayMode(avplay, dimensions);
  }

  async play(url, isFullScreen = false) {
    // Ensure player object is initialized
    this._initializePlayerObject();

    if (this._currentPlayUrl !== url) {
      this._lastPreviewCoords = null;
      this._lastVideoDimensions = null;
    }
    this._currentPlayUrl = url;

    this.updatePlayerState({ url });

    const myRequestId = getNextPlayRequestId();

    // Get current player state to check if we should reset busy flag
    let playerState = this.getPlayerState();
    const currentState = playerState.gAVplayObj?.getState?.() || '';
    if (currentState === 'NONE' || currentState === 'READY' || currentState === 'IDLE') {
      this.updatePlayerState({ isPlayerBusy: false });
    }

    // Reset player state if necessary (stops/closes if not in valid state)
    this._resetPlayerState();

    // Get fresh state after potential reset
    playerState = this.getPlayerState();

    // Don't block on isPlayerBusy - rely on playRequestId to abort old requests
    // if (playerState.isPlayerBusy) {
    //   console.warn('Tizen: Player is currently busy. Aborting this call.');
    //   return;
    // }

    this.updatePlayerState({ isPlayerBusy: true });

    try {
      await this._playWithRetry(myRequestId, isFullScreen);
    } finally {
      // Reset busy flag if this is still the current request
      const finalState = this.getPlayerState();
      if (myRequestId === finalState.playRequestId) {
        this.updatePlayerState({ isPlayerBusy: false });
      }
    }
  }

  async _playWithRetry(myRequestId, isFullScreen, retryCount = 0) {
    // Get fresh player state for each attempt
    const playerState = this.getPlayerState();
    const tizenPlayer = this;

    const listener = {
      onbufferingstart: () => console.log('Tizen: Buffering start'),
      onbufferingprogress: (percent) => console.log('Tizen: Buffering progress:', percent),
      onbufferingcomplete: () => {
        console.log('Tizen: Buffering complete');
        if (myRequestId === tizenPlayer.getPlayerState().playRequestId) {
          markChannelStreamReady(myRequestId);
          tizenPlayer._applyHttpAspectMode(myRequestId);
          if (!isFullScreen) {
            tizenPlayer._applyPreviewRect(tizenPlayer._lastPreviewCoords);
          }
        }
      },
      oncurrentplaytime: (currentTime) => {
        console.log('Tizen: Current playtime:', currentTime);
        if (currentTime > 0 && myRequestId === tizenPlayer.getPlayerState().playRequestId) {
          tizenPlayer.updatePlayerState({ httpPlayTimeMs: currentTime, httpLastPlayAt: Date.now() });
          markChannelStreamReady(myRequestId);
        }
      },
      onevent: (eventType, eventData) => {
        console.log(`Tizen: Event: ${eventType}, Data: ${eventData}`);
        if (myRequestId !== tizenPlayer.getPlayerState().playRequestId) {
          return;
        }

        var resolution = parseTizenVideoResolution(eventType, eventData);
        if (resolution) {
          tizenPlayer._lastVideoDimensions = resolution;
          tizenPlayer._applyHttpAspectMode(myRequestId);
        }

        if (
          !isFullScreen &&
          (eventType === 'PLAYER_MSG_RESOLUTION_CHANGED' ||
            eventType === 'PLAYER_MSG_BITRATE_CHANGE')
        ) {
          tizenPlayer._applyPreviewRect(tizenPlayer._lastPreviewCoords);
        }
      },
      onerror: createHttpStreamErrorHandler(this, myRequestId),
      onsubtitlechange: () => console.log('Tizen: Subtitle changed'),
      ondrmevent: (drmEvent, drmData) =>
        console.log('Tizen: DRM Event:', drmEvent, 'Data:', drmData),
      onstreamcompleted: createHttpStreamCompletedHandler(this, myRequestId),
    };

    try {
      // Check if we've already been superseded before even starting
      const preCheckState = this.getPlayerState();
      if (myRequestId !== preCheckState.playRequestId) {
        console.warn('Tizen: Aborting before start - newer request already in progress');
        return;
      }

      const initialState = playerState.gAVplayObj.getState();
      console.log('Tizen: Initial player state:', initialState);

      // Always stop/close for clean state, unless it's already NONE
      // This prevents InvalidStateError on open()
      if (initialState !== 'NONE') {
        try {
          console.warn('Tizen: Ensuring clean state - stopping and closing player');
          playerState.gAVplayObj.stop();
          playerState.gAVplayObj.close();
          // Slightly longer delay to ensure state transition completes
          await this.delay(100);
        } catch (cleanupErr) {
          // Ignore errors during cleanup - player might already be closed
          console.warn('Tizen: Cleanup warning (safe to ignore):', cleanupErr.name);
        }
      }

      playerState.gAVplayObj.open(playerState.url);
      console.log('Tizen: open() called');

      // Check immediately if superseded - no delay if we're already outdated
      const immediateCheck = this.getPlayerState();
      if (myRequestId !== immediateCheck.playRequestId) {
        console.warn('Tizen: Aborting playback - newer request in progress');
        return;
      }

      // Minimal delay to let open() complete - only if we're still current
      await this.delay(50);

      // Check if another playback has been requested
      const updatedState = this.getPlayerState();
      if (myRequestId !== updatedState.playRequestId) {
        console.warn('Tizen: Aborting playback - newer request in progress');
        return;
      }

      const stateAfterOpen = playerState.gAVplayObj.getState();
      console.log('Tizen: State after open():', stateAfterOpen);

      if (stateAfterOpen !== 'IDLE') {
        throw new Error(
          `Tizen: Player did not reach IDLE state after open(). Got: ${stateAfterOpen}`
        );
      }

      // Absorb short UDP jitter on Hotel TV (must be IDLE, before prepare).
      applyAvplayBuffering(playerState.gAVplayObj, 'Tizen');

      // Set display coordinates
      // For fullscreen: use full screen dimensions
      // For preview: calculate based on av-player element's actual position
      let coords;
      if (isFullScreen) {
        coords = { x: 0, y: 0, width: 1920, height: 1080 };
      } else if (this._lastPreviewCoords) {
        coords = this._lastPreviewCoords;
      } else {
        coords = await this._waitForStablePreviewCoords();
      }

      const player = document.getElementById('av-player');
      if (player) {
        if (isFullScreen) {
          player.classList.add('fullscreenMode');
        } else {
          player.classList.remove('fullscreenMode');
        }
      }

      this._applyPreviewRect(coords, isFullScreen);
      playerState.gAVplayObj.setListener(listener);

      console.log('Tizen: Display rect set to:', coords, 'isFullScreen:', isFullScreen);
      console.log('Tizen: Calling prepareAsync()...');

      // Wrap prepareAsync in a promise with timeout
      await this._prepareAsyncWithTimeout(myRequestId, retryCount, isFullScreen);
    } catch (err) {
      console.error('Tizen: Exception caught:', err);

      if (
        (err.name === 'InvalidStateError' ||
          err.name === 'InvalidAccessError' ||
          err.message?.includes('Invalid state') ||
          err.message?.includes('Invalid operation') ||
          err.message?.includes('prepareAsync timeout')) &&
        retryCount < this.maxRetries
      ) {
        console.warn(`Tizen: Retrying due to ${err.name || err.message}... (${retryCount + 1}/${this.maxRetries})`);
        // Reduced delay for faster retry - 100ms instead of 500ms
        await this.delay(100);
        return this._playWithRetry(myRequestId, isFullScreen, retryCount + 1);
      } else {
        console.error('Tizen: Unrecoverable error or max retries reached');
        throw err;
      }
    }
  }

  _prepareAsyncWithTimeout(myRequestId, retryCount, isFullScreen) {
    const playerState = this.getPlayerState();
    const tizenPlayer = this;

    return new Promise((resolve, reject) => {
      var settled = false;
      function settle(fn, arg) {
        if (settled) return;
        settled = true;
        clearTimeout(prepareTimeout);
        fn(arg);
      }

      const prepareTimeout = setTimeout(() => {
        console.warn('Tizen: prepareAsync timeout - force settle');
        try {
          playerState.gAVplayObj.stop();
          playerState.gAVplayObj.close();
        } catch (e) {}

        // Always settle — never leave play() hanging (app-wide freeze)
        if (myRequestId !== tizenPlayer.getPlayerState().playRequestId) {
          settle(resolve);
          return;
        }
        settle(reject, new Error('prepareAsync timeout'));
      }, 5000);

      playerState.gAVplayObj.prepareAsync(
        () => {
          const checkState = tizenPlayer.getPlayerState();
          if (myRequestId !== checkState.playRequestId) {
            console.warn('Tizen: Aborting prepareAsync callback - newer request in progress');
            settle(resolve);
            return;
          }

          const readyState = playerState.gAVplayObj.getState();
          console.log('Tizen: prepareAsync success. State:', readyState);

          if (readyState === 'READY' || readyState === 'IDLE') {
            try {
              playerState.gAVplayObj.play();
              console.log('Tizen: Playback started successfully');

              if (!isFullScreen && myRequestId === playerState.playRequestId) {
                tizenPlayer._applyPreviewRect(tizenPlayer._lastPreviewCoords);
                setTimeout(() => {
                  if (myRequestId === tizenPlayer.getPlayerState().playRequestId) {
                    tizenPlayer._applyPreviewRect(tizenPlayer._lastPreviewCoords);
                  }
                }, 100);
              }

              tizenPlayer._applyHttpAspectMode(myRequestId);

              setTimeout(() => {
                const timeCheckState = tizenPlayer.getPlayerState();
                if (myRequestId === timeCheckState.playRequestId) {
                  const currentState = playerState.gAVplayObj.getState();
                  console.log('Tizen: Play state check after 3s:', currentState);
                  if (currentState !== 'PLAYING') {
                    console.warn('Tizen: Player not in PLAYING state, may be stuck');
                  }
                }
              }, 3000);

              settle(resolve);
            } catch (playErr) {
              console.error('Tizen: play() failed after READY:', playErr);
              settle(reject, playErr);
            }
          } else {
            console.warn('Tizen: Unexpected state after prepareAsync:', readyState);
            settle(reject, new Error('Unexpected state: ' + readyState));
          }
        },
        () => {
          const checkState = tizenPlayer.getPlayerState();
          if (myRequestId !== checkState.playRequestId) {
            console.warn('Tizen: Aborting prepareAsync error callback - newer request in progress');
            settle(resolve);
            return;
          }

          console.error('Tizen: prepareAsync failed - stream may be invalid or unreachable');
          settle(reject, new Error('prepareAsync failed'));
        }
      );
    });
  }

  /**
   * Reset player state if necessary - matches old resetPlayerState() logic
   * Called before starting new playback to ensure clean state
   */
  _resetPlayerState() {
    const playerState = this.getPlayerState();
    const state = playerState.gAVplayObj?.getState();

    if (state && state !== 'NONE' && state !== 'IDLE') {
      try {
        playerState.gAVplayObj.stop();
        playerState.gAVplayObj.close();
        console.log('Tizen: Player reset with stop and close.');
      } catch (e) {
        console.warn('Tizen: Error during reset:', e);
      }
    }
  }

  async stop() {
    const playerState = this.getPlayerState();
    if (playerState.gAVplayObj) {
      try {
        playerState.gAVplayObj.stop();
        playerState.gAVplayObj.close();
        console.log('Tizen: player stopped');
      } catch (e) {
        console.warn('Tizen: error stopping player:', e);
      }
    }
  }

  async setDisplayRect(coords) {
    const playerState = this.getPlayerState();
    if (playerState.gAVplayObj) {
      try {
        const x = coords.x ?? coords.left ?? 0;
        const y = coords.y ?? coords.top ?? 0;
        playerState.gAVplayObj.setDisplayRect(x, y, coords.width, coords.height);
        console.log('Tizen: display rect set:', { x, y, width: coords.width, height: coords.height });
      } catch (e) {
        console.error('Tizen: error setting display rect:', e);
      }
    }
  }

  async toggleFullscreen() {
    const playerState = this.getPlayerState();
    const newFullScreenState = !playerState.displayFullScreen;
    this.updatePlayerState({ displayFullScreen: newFullScreenState });

    const player = document.getElementById('av-player');

    // Update CSS class FIRST, before calculating coordinates
    if (newFullScreenState) {
      player?.classList.add('fullscreenMode');
    } else {
      player?.classList.remove('fullscreenMode');
    }

    // Longer delay when going back to preview to allow HeaderBar to render
    // and layout to stabilize before calculating coordinates
    const delayTime = newFullScreenState ? 50 : 150;
    await this.delay(delayTime);
    
    // Now calculate coordinates based on the updated CSS and layout
    const coords = newFullScreenState
      ? { x: 0, y: 0, width: 1920, height: 1080 }
      : await this._waitForStablePreviewCoords();

    console.log('Tizen: toggleFullscreen ->', newFullScreenState ? 'FULLSCREEN' : 'PREVIEW', coords);

    this._applyPreviewRect(coords, newFullScreenState);
  }
}

export default TizenPlayer;
